import { cn } from "@groot/ui/lib/utils";
import type { ReactNode } from "react";

const MAX_WIDTH_CLASSES = {
  "3xl": "max-w-3xl",
  "4xl": "max-w-4xl",
  "5xl": "max-w-5xl",
  "6xl": "max-w-6xl",
  "7xl": "max-w-7xl",
} as const;

interface PageProps {
  children: ReactNode;
  className?: string;
  /**
   * Content max-width. Defaults to `7xl` (list / dashboard / table pages).
   * Use a narrower value for detail / form pages that read better tighter.
   */
  maxWidth?: keyof typeof MAX_WIDTH_CLASSES;
}

/**
 * Max-width page wrapper for non-full-height pages.
 *
 * The shell `<Layout padded>` owns horizontal + vertical padding (matches the
 * groot boilerplate). This wrapper only applies the max-width + centering so
 * there is no double padding. Pair with `<PageHeader>` from
 * `@groot/shell/components/layout` for the title block.
 *
 * @example
 * ```tsx
 * <Page>
 *   <PageHeader title="Accounts" description="Manage your accounts" />
 *   <div className="space-y-6">...</div>
 * </Page>
 * ```
 */
export function Page({ children, className, maxWidth = "7xl" }: PageProps) {
  return (
    <div className={cn("mx-auto w-full", MAX_WIDTH_CLASSES[maxWidth], className)}>{children}</div>
  );
}
